// Just important imports
import React, { useState, useEffect, useLayoutEffect } from "react";
import { HashRouter as Router, Route, Routes, useLocation } from "react-router-dom";
import "./App.css";

// The primary pages
import HomePage from "./pages/HomePage";
import AboutPage from "./pages/About";
import WritingIndexPage from "./pages/writing/WritingIndex";
import WritingEntryPage from "./pages/writing/WritingEntry";
import PrivateForPage from "./pages/PrivateForPage";
import ComputationalDesignPage from "./pages/ComputationalDesign";
import CompDesign2Page from "./pages/CompDesign2";

// The components
import Header from "./designSystem/components/layout/Header";
import Footer from "./designSystem/components/layout/Footer";
import CustomCursorV2 from "./designSystem/components/layout/CustomCursorV2.js";
import LoadingComponent from "./designSystem/components/layout/LoadingComponent.js";
import CustomScrollbar from "./designSystem/components/layout/CustomScrollbar.js";
import ScrollToTop from "./designSystem/components/layout/ScrollToTop";
// Design system preview pages (visible in production — discoverable via direct URL)
import DesignSystemPage from "./designSystem/preview/DesignSystem.js";
import ProjectBlocksPreviewPage from "./designSystem/preview/ProjectBlocksPreview.js";

// The projects
import EchoPage from "./pages/projects/ECHO.js";
import CowesWeekPavilionPage from "./pages/projects/CowesWeekPavilion.js";
import DropletsClashPage from "./pages/projects/DropletsClash.js";
import GENxFacadeSolutionPage from "./pages/projects/GENxFacadeSolution.js";
import FourSeasonsFacadePage from "./pages/projects/FourSeasonsFacade.js";
import CliffYouthCenterPage from "./pages/projects/CliffYouthCenter.js";
import APEToolsPage from "./pages/projects/ApeTools.js";
import WEAVEPage from "./pages/projects/WEAVE.js";
import SILTMiddelkerkePage from "./pages/projects/SILTMiddelkerke.js";
import CTEPPage from "./pages/projects/CTEP.js";
import TheCutawayPage from "./pages/projects/TheCutaway.js";
import AquaCastPage from "./pages/projects/AquaCast.js";

/** Locks a route to the viewport; body does not scroll — only inner columns may. */
function useViewportLock() {
  useLayoutEffect(() => {
    const prevHtmlOverflow = document.documentElement.style.overflow;
    const prevBodyOverflow = document.body.style.overflow;
    document.documentElement.style.overflow = "hidden";
    document.body.style.overflow = "hidden";
    return () => {
      document.documentElement.style.overflow = prevHtmlOverflow;
      document.body.style.overflow = prevBodyOverflow;
    };
  }, []);
}

function AboutShell() {
  useViewportLock();
  return (
    <div className="flex h-[100dvh] max-h-[100dvh] flex-col overflow-hidden bg-white dark:bg-black">
      <AboutPage />
      <Footer />
    </div>
  );
}

function PrivateForShell() {
  useViewportLock();
  return (
    <div className="flex h-[100dvh] max-h-[100dvh] flex-col overflow-hidden bg-white dark:bg-black">
      <PrivateForPage />
      <Footer />
    </div>
  );
}

function AppRoutes() {
  const location = useLocation();
  // The landing scrolls like the rest of the site now, so only About and the
  // private share pages are still fitted to the viewport.
  const isSinglePageRoute =
    location.pathname === "/About" || location.pathname.startsWith("/for/");

  const [loading] = useState(true);

  return (
    <div className={`App dark:bg-black dark:text-white ${isSinglePageRoute ? "min-h-0" : "min-h-screen"}`}>
      {loading && <LoadingComponent />}

      <Header />
      <Routes>
        <Route path="/" element={<HomePage />} />
        {/* Kept so older shared links still land on the work. */}
        <Route path="/workSection" element={<HomePage />} />
        <Route path="/About" element={<AboutShell />} />
        <Route path="/Writing" element={<WritingIndexPage />} />
        <Route path="/Writing/:slug" element={<WritingEntryPage />} />
        <Route path="/ECHO" element={<EchoPage />} />
        <Route path="/CowesWeekPavilion" element={<CowesWeekPavilionPage />} />
        <Route path="/DropletsClash" element={<DropletsClashPage />} />
        <Route path="/GENxFacadeSolution" element={<GENxFacadeSolutionPage />} />
        <Route path="/FourSeasonsFacade" element={<FourSeasonsFacadePage />} />
        <Route path="/CliffYouthCenter" element={<CliffYouthCenterPage />} />
        <Route path="/CTEP" element={<CTEPPage />} />
        <Route path="/SILTMiddelkerke" element={<SILTMiddelkerkePage />} />
        <Route path="/APETools" element={<APEToolsPage />} />
        <Route path="/WEAVE" element={<WEAVEPage />} />
        <Route path="/TheCutaway" element={<TheCutawayPage />} />
        <Route path="/AquaCast" element={<AquaCastPage />} />
        <Route path="/ComputationalDesign" element={<ComputationalDesignPage />} />
        <Route path="/CompDesign2" element={<CompDesign2Page />} />
        <Route path="/for/:slug" element={<PrivateForShell />} />
        <Route path="/design-system" element={<DesignSystemPage />} />
        <Route path="/project-blocks" element={<ProjectBlocksPreviewPage />} />
      </Routes>

      {!isSinglePageRoute && <CustomScrollbar />}
      {!isSinglePageRoute && <Footer />}
    </div>
  );
}

function App() {
  useEffect(() => {
    if ("scrollRestoration" in window.history) {
      const prev = window.history.scrollRestoration;
      window.history.scrollRestoration = "manual";
      return () => {
        window.history.scrollRestoration = prev || "auto";
      };
    }
  }, []);

  return (
    <Router>
      <ScrollToTop />

      <CustomCursorV2 />

      <AppRoutes />
    </Router>
  );
}

export default App;
